import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '@/utils/api'

// Async thunks
export const fetchForms = createAsyncThunk(
  'forms/fetchForms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/forms')
      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchForm = createAsyncThunk(
  'forms/fetchForm',
  async (formId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/forms/${formId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const createForm = createAsyncThunk(
  'forms/createForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/forms', formData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const updateForm = createAsyncThunk(
  'forms/updateForm',
  async ({ formId, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/forms/${formId}`, formData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

// Add this submitForm async thunk
export const submitForm = createAsyncThunk(
  'forms/submitForm',
  async ({ formId, data }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/forms/${formId}/submit`, data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

// Submit modernization form (accepts FormData)
export const submitModernizationForm = createAsyncThunk(
  'forms/submitModernizationForm',
  async (formData, { rejectWithValue }) => {
    try {
      // axios will set multipart headers for FormData
      const response = await api.post('/modernization', formData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message })
    }
  }
)

export const deleteForm = createAsyncThunk(
  'forms/deleteForm',
  async (formId, { rejectWithValue }) => {
    try {
      await api.delete(`/forms/${formId}`)
      return formId
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)
export const fetchUserSubmissions = createAsyncThunk(
  'forms/fetchUserSubmissions',
  async (formId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/submissions/user?form_id=${formId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

const formSlice = createSlice({
  name: 'forms',
  initialState: {
    forms: [],
    currentForm: null,
    submissions: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentForm: (state, action) => {
      state.currentForm = action.payload
    },
    addSubmission: (state, action) => {
      state.submissions.push(action.payload)
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch forms
      .addCase(fetchForms.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchForms.fulfilled, (state, action) => {
        state.loading = false
        state.forms = action.payload
      })
      .addCase(fetchForms.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch forms'
      })
      // Fetch form
      .addCase(fetchForm.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchForm.fulfilled, (state, action) => {
        state.loading = false
        state.currentForm = action.payload
      })
      .addCase(fetchForm.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch form'
      })
      // Submit form
      .addCase(submitForm.pending, (state) => {
        state.loading = true
      })
      .addCase(submitForm.fulfilled, (state, action) => {
        state.loading = false
        state.submissions.push(action.payload)
      })
      .addCase(submitForm.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to submit form'
      })
      // Submit modernization form
      .addCase(submitModernizationForm.pending, (state) => {
        state.loading = true
      })
      .addCase(submitModernizationForm.fulfilled, (state, action) => {
        state.loading = false
        // push the returned submission or response
        state.submissions.push(action.payload)
      })
      .addCase(submitModernizationForm.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to submit modernization form'
      })
      // Create form
      .addCase(createForm.fulfilled, (state, action) => {
        state.forms.push(action.payload)
      })
      // Update form
      .addCase(updateForm.fulfilled, (state, action) => {
        const index = state.forms.findIndex(form => form.id === action.payload.id)
        if (index !== -1) {
          state.forms[index] = action.payload
        }
        state.currentForm = action.payload
      })
      // Delete form
      .addCase(deleteForm.fulfilled, (state, action) => {
        state.forms = state.forms.filter(form => form.id !== action.payload)
      })
  },
})

// Make sure to export the submitForm action
export const { clearError, setCurrentForm, addSubmission } = formSlice.actions
export default formSlice.reducer